import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import type { AuditLog } from '../../types/api';

export const AdminAuditLogsPage: React.FC = () => {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);

    const loadLogs = async () => {
        setLoading(true);
        try {
            const res = await api.adminGetAuditLogs(page);
            setLogs(res.data);
            if (res.meta) {
                setTotalPages(res.meta.last_page);
            }
        } catch {
            // error handled
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLogs();
    }, [page]);

    return (
        <div className="space-y-8">
            <div>
                <h1 className="text-3xl font-extrabold text-white tracking-tight">Audit & Security Logs</h1>
                <p className="text-sm text-slate-400 mt-1">Immutable administrative action trails, login attempts, and record updates.</p>
            </div>

            <div className="p-6 rounded-3xl glass-card overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead>
                        <tr className="border-b border-slate-800 text-xs text-slate-400 uppercase tracking-wider">
                            <th className="py-4 font-semibold">Timestamp</th>
                            <th className="py-4 font-semibold">User</th>
                            <th className="py-4 font-semibold">Action</th>
                            <th className="py-4 font-semibold">IP Address</th>
                            <th className="py-4 font-semibold">Details</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                        {logs.length > 0 ? (
                            logs.map(log => (
                                <tr key={log.id} className="hover:bg-slate-800/20">
                                    <td className="py-4 text-xs font-mono text-slate-400">
                                        {new Date(log.created_at).toLocaleString()}
                                    </td>
                                    <td className="py-4 font-semibold text-white">
                                        {log.user?.name || 'System / Guest'}
                                    </td>
                                    <td className="py-4 font-bold text-emerald-400 text-xs">
                                        {log.action}
                                    </td>
                                    <td className="py-4 text-xs font-mono text-slate-500">
                                        {log.ip_address || '—'}
                                    </td>
                                    <td className="py-4 text-xs text-slate-400 max-w-sm truncate" title={log.details || ''}>
                                        {log.details || '—'}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={5} className="py-8 text-center text-slate-500">
                                    {loading ? 'Loading audit trails...' : 'No audit logs recorded yet.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-end gap-2 text-xs">
                        <button
                            type="button"
                            disabled={page <= 1}
                            onClick={() => setPage(page - 1)}
                            className="px-3 py-1.5 rounded bg-slate-900 border border-slate-800 text-slate-400 disabled:opacity-40"
                        >
                            Previous
                        </button>
                        <span className="text-slate-400">Page {page} of {totalPages}</span>
                        <button
                            type="button"
                            disabled={page >= totalPages}
                            onClick={() => setPage(page + 1)}
                            className="px-3 py-1.5 rounded bg-slate-900 border border-slate-800 text-slate-400 disabled:opacity-40"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
